// Auto-mounted by AdminPersistentLayout. Don't import elsewhere —
// enforced by Biome's noRestrictedImports rule in biome.json.

import { toast } from "@admin-ui/components/primitives/application/toast";
import { usePage } from "@inertiajs/react";
import { useEffect, useRef } from "react";
import { type FlashAlert, isFlashMessage, normalizeFlashValue, type SharedProps } from "@/types";

export function useAdminFlashNotifications() {
  const { flash } = usePage<SharedProps>().props;
  const processedRef = useRef<string | null>(null);

  useEffect(() => {
    const fingerprint = JSON.stringify(flash);
    if (fingerprint === processedRef.current) return;
    processedRef.current = fingerprint;

    const toastNotification = normalizeFlashValue(flash.toast);
    if (toastNotification) toast.success(toastNotification.title, { description: toastNotification.description });

    const alertNotification = normalizeFlashValue(flash.alert);
    if (alertNotification) {
      toast.error(alertNotification.title, {
        description: alertDescription(flash.alert, alertNotification.description),
      });
    }
  }, [flash]);
}

// A refused write answers with its reasons as a list, and dropping them leaves
// the admin only the headline. Rendered as the front-of-house alert renders
// one (see use-flash-alerts.ts), which sonner allows: its description takes a
// node.
function alertDescription(value: FlashAlert | undefined, fallback: string) {
  const list = isFlashMessage(value) ? value.list : undefined;
  if (!list?.length) return fallback;

  return (
    <ul className="flex list-disc flex-col gap-0.5 ps-4">
      {list.map((reason) => (
        <li key={reason}>{reason}</li>
      ))}
    </ul>
  );
}
